import React, { useState } from "react";
import { Link } from "react-router-dom";
import { formatCurrency, formatPercent } from "@/utils/formatters";
import { formatDate } from "@/utils/timezone";
import ColoredCurrency from "../ColoredCurrency";
import ColoredPercent from "../ColoredPercent";

/**
 * Display a single date or date range for a trade
 */
const TradeDateCell = ({ firstDate, lastDate }) => {
  if (!firstDate) return <span>-</span>;

  const formattedFirst = formatDate(firstDate, "M/d/yy");

  if (!lastDate || firstDate === lastDate) {
    return <span>{formattedFirst}</span>;
  }

  const formattedLast = formatDate(lastDate, "M/d/yy");
  return (
    <span>
      {formattedFirst}
      <br />
      <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
        to {formattedLast}
      </span>
    </span>
  );
};

/**
 * Reusable Trade Breakdown table with sortable columns
 */
const TradeBreakdown = ({ contributors = [], accountId }) => {
  const [sortColumn, setSortColumn] = useState("period_pnl");
  const [sortDirection, setSortDirection] = useState("desc");

  if (!contributors || contributors.length === 0) {
    return (
      <div
        className="text-muted"
        style={{ textAlign: "center", padding: "2rem" }}
      >
        No trade data available
      </div>
    );
  }

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New column, default to descending
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const getSortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  const sortedContributors = [...contributors].sort((a, b) => {
    let aValue, bValue;

    switch (sortColumn) {
      case "date":
        aValue = a.first_sell_date || "";
        bValue = b.first_sell_date || "";
        break;
      case "ticker":
        aValue = a.ticker || "";
        bValue = b.ticker || "";
        break;
      case "period_pnl":
        aValue = a.period_pnl || 0;
        bValue = b.period_pnl || 0;
        break;
      case "period_pnl_percent":
        aValue = a.period_pnl_percent || 0;
        bValue = b.period_pnl_percent || 0;
        break;
      case "current_value":
        aValue = a.current_value || 0;
        bValue = b.current_value || 0;
        break;
      case "quantity":
        aValue = a.quantity || 0;
        bValue = b.quantity || 0;
        break;
      case "contribution_percent":
        aValue = a.contribution_percent || 0;
        bValue = b.contribution_percent || 0;
        break;
      default:
        aValue = 0;
        bValue = 0;
    }

    // String comparison
    if (typeof aValue === "string") {
      return sortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    // Numeric comparison
    return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
  });

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="performance-table data-table">
        <thead>
          <tr>
            <th
              onClick={() => handleSort("date")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Date {getSortIndicator("date")}
            </th>
            <th
              onClick={() => handleSort("ticker")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Stock {getSortIndicator("ticker")}
            </th>
            <th
              onClick={() => handleSort("period_pnl")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              P&L $ {getSortIndicator("period_pnl")}
            </th>
            <th
              onClick={() => handleSort("period_pnl_percent")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              P&L % {getSortIndicator("period_pnl_percent")}
            </th>
            <th
              onClick={() => handleSort("current_value")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Proceeds {getSortIndicator("current_value")}
            </th>
            <th
              onClick={() => handleSort("quantity")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Qty Sold {getSortIndicator("quantity")}
            </th>
            <th
              onClick={() => handleSort("contribution_percent")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Contribution {getSortIndicator("contribution_percent")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedContributors.map((contributor, index) => (
            <tr key={index}>
              <td>
                <TradeDateCell
                  firstDate={contributor.first_sell_date}
                  lastDate={contributor.last_sell_date}
                />
              </td>
              <td>
                {contributor.ticker && accountId ? (
                  <Link
                    to={`/ticker/${contributor.ticker}?accountId=${accountId}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <strong style={{ cursor: "pointer" }}>
                      {contributor.ticker}
                    </strong>
                  </Link>
                ) : (
                  <strong>{contributor.ticker}</strong>
                )}
              </td>
              <td>
                <ColoredCurrency value={contributor.period_pnl} />
              </td>
              <td>
                {contributor.period_pnl_percent === 0 &&
                contributor.period_pnl !== 0 ? (
                  <span
                    className="text-muted"
                    title="Zero cost basis (free shares)"
                  >
                    N/A
                  </span>
                ) : (
                  <ColoredPercent value={contributor.period_pnl_percent} />
                )}
              </td>
              <td>{formatCurrency(contributor.current_value)}</td>
              <td>{Math.round(contributor.quantity)} shares</td>
              <td>{formatPercent(contributor.contribution_percent)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TradeBreakdown;
